import type { MappedFilterValues } from "@/composables/filter/filterValueTransformers";
import type { SearchRequest } from "@/services/collection/item/elastic/collectionItemElasticApi";
import { ParameterSlug_Category, ParameterSlug_MarketplacePrice } from "@/services/filter/filterHelpers";
import type { FilterValues } from "@/services/filter/filterTypes";
import type { SavedSearchCreateRequest, SavedSearchCriteria, SavedSearchNameSource, SavedSearchParameters } from "@/services/user/savedSearch/savedSearchTypes";

const RangeKeyPattern = /^(.+)_(min|max)$/;

/** Prices travel through the saved-search API in cents; everything shown to the user is whole units. */
export const CentsPerUnit = 100;

const NameCounterPattern = /^(.*) \((\d+)\)$/;

/**
 * The listing request's filter map (`slug` → values, `slug_min`/`slug_max` → one bound each, prices
 * already in cents) in the shape the saved-search API stores: keyed by slug, ranges as `{min, max}`.
 */
export const createSavedSearchParameters = (filters: MappedFilterValues): SavedSearchParameters => {
  const parameters: SavedSearchParameters = {};

  for (const [key, values] of Object.entries(filters)) {
    const rangeMatch = key.match(RangeKeyPattern);
    if (rangeMatch === null) {
      parameters[key] = values;
      continue;
    }

    const [, slug, bound] = rangeMatch;
    const range = parameters[slug];
    const existing = range !== undefined && !Array.isArray(range) ? range : {};
    parameters[slug] = { ...existing, [bound]: values[0] };
  }

  return parameters;
};

/** What the marketplace page is showing, as a request to save it under `name`. */
export const createSavedSearchCreateRequest = (request: SearchRequest, name: string | null): SavedSearchCreateRequest => ({
  name,
  query: request.query,
  categoryId: request.categoryId,
  collectibleIds: request.collectibleIds,
  ownerId: request.ownerId,
  sort: request.sort,
  parameters: createSavedSearchParameters(request.filters ?? {}),
});

/**
 * URL filter values that put a saved search back on the marketplace page: ranges back to
 * `slug_min`/`slug_max`, the price back from cents to whole units, the category by its URL slug.
 */
export const createFilterValuesFromCriteria = (criteria: SavedSearchCriteria, categoryUrl: string | undefined): FilterValues => {
  const values: FilterValues = {};

  if (categoryUrl !== undefined) {
    values[ParameterSlug_Category] = categoryUrl;
  }

  for (const [slug, parameter] of Object.entries(criteria.parameters)) {
    if (Array.isArray(parameter)) {
      values[slug] = parameter;
      continue;
    }

    const isPrice = slug === ParameterSlug_MarketplacePrice;
    for (const bound of ["min", "max"] as const) {
      const raw = parameter[bound];
      if (raw === undefined) {
        continue;
      }
      const numeric = Number(raw);
      values[`${slug}_${bound}`] = String(isPrice && !isNaN(numeric) ? numeric / CentsPerUnit : raw);
    }
  }

  return values;
};

const createNameWithFilters = (source: SavedSearchNameSource, filterCount: number): string => [source.subject, ...source.filters.slice(0, filterCount), ...(source.price !== undefined ? [source.price] : [])].join(" ");

/** The short name from `source`, e.g. "Charizard do 2 000 Kč". */
export const createSavedSearchName = (source: SavedSearchNameSource): string => createNameWithFilters(source, source.shownFilterCount);

const normalizeName = (name: string): string => name.trim().toLocaleLowerCase();

/**
 * A generated name none of `existingNames` has. A clash first gets the next filter values, as they
 * are what tells the two searches apart; only then a " (2)" counter, one above the highest used.
 */
export const createUniqueSavedSearchName = (source: SavedSearchNameSource, existingNames: string[]): string => {
  const taken = new Set(existingNames.map(normalizeName));
  const name = createSavedSearchName(source);
  if (!taken.has(normalizeName(name))) {
    return name;
  }

  for (let count = source.shownFilterCount + 1; count <= source.filters.length; count++) {
    const longerName = createNameWithFilters(source, count);
    if (!taken.has(normalizeName(longerName))) {
      return longerName;
    }
  }

  const base = normalizeName(name);
  let highestCounter = 1;
  for (const existing of taken) {
    const match = existing.match(NameCounterPattern);
    if (match !== null && match[1] === base) {
      highestCounter = Math.max(highestCounter, Number(match[2]));
    }
  }
  return `${name} (${highestCounter + 1})`;
};

type ComparableCriteria = Pick<SavedSearchCreateRequest, "query" | "categoryId" | "collectibleIds" | "ownerId" | "parameters">;

const createCriteriaKey = ({ query, categoryId, collectibleIds, ownerId, parameters }: ComparableCriteria): string => {
  const normalizedParameters = Object.keys(parameters)
    .sort()
    .map((slug) => {
      const parameter = parameters[slug];
      if (Array.isArray(parameter)) {
        return parameter.length > 0 ? [slug, parameter.map(String).sort()] : undefined;
      }
      return parameter.min !== undefined || parameter.max !== undefined ? [slug, parameter.min ?? null, parameter.max ?? null] : undefined;
    })
    .filter((parameter) => parameter !== undefined);

  return JSON.stringify({
    query: query?.trim().toLocaleLowerCase() || null,
    categoryId: categoryId ?? null,
    collectibleIds: (collectibleIds ?? []).map(String).sort(),
    ownerId: ownerId ?? null,
    parameters: normalizedParameters,
  });
};

/** Whether both match the same items. The sort is left out: it only orders them, so both would notify about the same new items. */
export const isSameSavedSearchCriteria = (request: SavedSearchCreateRequest, criteria: SavedSearchCriteria): boolean => createCriteriaKey(request) === createCriteriaKey(criteria);
